import "moment-timezone";
import moment from "moment";
import SmsTemplate from "./sms-template";
import Sms from "./sms";
import Msg from "./message";
import EmailTemplate from "./email-template";
import Email from "./email";
import Api from "./api";
import { enumIncludes, enumValues, regexpFullTest, sortBy } from "../utils";
import ISmsTemplate from "../interface/sms-template";
import IRelationPathField from "../interface/relation-path-field";
import IFeedbackResult from "../interface/feedback-result";
import IFeedbackKey from "../interface/feedback-key";
import IFeedbackHistoryItem from "../interface/feedback-history-item";
import IFeedbackDetails from "../interface/feedback-details";
import IFeedbackActionString from "../interface/feedback-action-string";
import IEmailTemplate from "../interface/email-template";
import IApiWebhookStatus from "../interface/api/api-webhook-status";
import IApiWebhookInfoHisto from "../interface/api/api-webhook-info-histo";
import IApiWebhookInfo from "../interface/api/api-webhook-info";
import IApiGroupKey from "../interface/api/api-group-key";
import Global from "../global";
import SortingType from "../enum/sorting-type";
import ServiceName from "../enum/service-name";
import Message from "../enum/message";
import FeedbackStatus from "../enum/feedback-status";
import FeedbackPeriodicity from "../enum/feedback-priodicity";
import EmailTemplateType from "../enum/email-template-type";

class FeedbackSingleton {

    public getKeys(entityId: number): IFeedbackKey[] {
        const result: IFeedbackKey[] = [];
        /* Collect Email Keys */
        result.push(...this.parseGroupKeys(Email.getEmissions(entityId), ServiceName.Email));
        /* Collect Sms Keys */
        result.push(...this.parseGroupKeys(Sms.getEmissions(entityId), ServiceName.Sms));
        return result.sort((a: IFeedbackKey, b: IFeedbackKey) => {
            return this.getStatHash(a).localeCompare(this.getStatHash(b));
        });
    }

    public async getResult(id: number): Promise<IFeedbackResult[]> {
        const groupKey: IFeedbackKey | null = this.getKey(id);
        if (groupKey) {
            const result: IApiWebhookStatus[] = await Api.getGroupKeysStatus(groupKey.service, id);
            return result.map((s: IApiWebhookStatus) => {
                return {
                    documentId: Number(s.docid),
                    status: Number(s.status) as FeedbackStatus,
                    address: s.key,
                    webhookId: Number(s.webhookid),
                    service: groupKey.service
                };
            });
        }
        return [];
    }

    public async getDetails(feedbacks: IFeedbackResult[]): Promise<IFeedbackDetails[]> {
        const result: IFeedbackDetails[] = [];
        const ids: number[] = feedbacks.map((w: IFeedbackResult) => w.webhookId);
        const service: ServiceName = feedbacks.length ? feedbacks[0].service : ServiceName.Email;
        const response: IApiWebhookInfo[] = await Api.getWebhooksInfo(service, ids);
        response.forEach((item: IApiWebhookInfo) => {
            const history: IFeedbackHistoryItem[] = item.histo.map((i: IApiWebhookInfoHisto) => {
                return {
                    info: i.info,
                    action: i.action,
                    date: new Date(i.date).toISOString()
                };
            }).reverse();
            result.push({
                id: item.id,
                date: history[history.length - 1].date,
                feedback: feedbacks.find((w: IFeedbackResult) => {
                    return w.webhookId === item.id;
                }) as IFeedbackResult,
                history
            });
        });
        return sortBy(result, 'id', SortingType.Descending);
    }

    public async deleteKey(id: number): Promise<number> {
        const key: IFeedbackKey | null = this.getKey(id);
        if (key) {
            await Api.groupKeyDelete(key.service, key.id);
        }
        return id;
    }

    /* Private Public Methods */

    public getKey(id: number): IFeedbackKey | null {
        const key: IApiGroupKey | null = Api.groupKeyGet(id);
        if (key) {
            return this.parseGroupKey(key);
        }
        return null;
    }

    public getCurrentActionString(tplId: number, periodicity: FeedbackPeriodicity = FeedbackPeriodicity.Month): string {
        return this.getActionString({
            timespan: this.getCurrentTimespan(periodicity),
            periodicity,
            text: null,
            tplId
        });
    }

    public async renameKeysToFixed(service: ServiceName, tpl: IEmailTemplate | ISmsTemplate): Promise<IFeedbackKey[]> {
        const keys: IFeedbackKey[] = this.getKeysByTemplateId(service, tpl);
        for (let i = 0; i < keys.length; i++) {
            const key: IFeedbackKey = keys[i];
            const action: string = this.getActionString({
                tplId: null,
                periodicity: key.periodicity,
                timespan: key.timespan,
                text: tpl.name
            });
            await Api.groupKeyRename(key.id, action);
        }
        return this.getKeysByTemplateId(service, tpl);
    }

    /* Private Methods */

    private getActionString(data: IFeedbackActionString): string {
        const tplId: string = (data.tplId || 0).toString();
        const timespan: string = data.timespan || Global.ACTION_STRING_EMPTY;
        const text: string = data.text || Global.ACTION_STRING_EMPTY;
        return [tplId, data.periodicity, timespan, text].join(Global.ACTION_STRING_SEPARATOR);
    }

    private getKeysByTemplateId(service: ServiceName, tpl: IEmailTemplate | ISmsTemplate): IFeedbackKey[] {
        return this.getKeys(tpl.entityId).filter((key: IFeedbackKey) => {
            return key.service === service && key.tpl && key.tpl.id === tpl.id
        });
    }

    private getCurrentTimespan(periodicity: FeedbackPeriodicity): string {
        const now: moment.Moment = moment();
        switch (periodicity) {
            case FeedbackPeriodicity.Year:
                return now.format('YYYY');
            case FeedbackPeriodicity.Quarter:
                return now.format('YYYY-Q');
            case FeedbackPeriodicity.Month:
                return now.format('YYYY-MM');
            case FeedbackPeriodicity.Week:
                return now.format('YYYY-WW');
            case FeedbackPeriodicity.Day:
                return now.format('YYYY-MM-DD');
            case FeedbackPeriodicity.Free:
                return Global.ACTION_STRING_EMPTY;
        }
    }

    private parseGroupKeys(paths: IRelationPathField[], service: ServiceName): IFeedbackKey[] {
        const result: IFeedbackKey[] = [];
        paths.forEach((path: IRelationPathField) => {
            const keys: IApiGroupKey[] = Api.groupKeyGetByPath(path.pathId, service);
            keys.forEach((key: IApiGroupKey) => {
                const groupKey: IFeedbackKey | null = this.parseGroupKey(key);
                if (groupKey) {
                    result.push(groupKey);
                }
            });
        });
        return result;
    }

    private parseGroupKey(key: IApiGroupKey): IFeedbackKey | null {
        const parts: string[] = key.action.split(Global.ACTION_STRING_SEPARATOR);
        if (parts.length === 4) {
            let tpl: IEmailTemplate | ISmsTemplate | null = null;
            let tplName: string = '';
            switch (key.service) {
                case ServiceName.Email:
                    tpl = EmailTemplate.get(Number(parts[0]));
                    if (tpl) {
                        if (tpl.type === EmailTemplateType.PublicFormNotification) {
                            tplName = Msg.get(Message.publicformnotification);
                        }
                        tplName = tpl.name;
                    }
                    break;
                case ServiceName.Sms:
                    tpl = SmsTemplate.get(Number(parts[0]));
                    if (tpl) {
                        tplName = tpl.name;
                    }
                    break;
            }
            if (enumIncludes(FeedbackPeriodicity, parts[1])) {
                const periodicity: FeedbackPeriodicity = parts[1] as FeedbackPeriodicity;
                const timespan: string = parts[2] === Global.ACTION_STRING_EMPTY ? '' : parts[2];
                const name: string = tplName || parts[3];
                const period: string = this.parseTimespan(periodicity, timespan);
                return { id: key.id, service: key.service, tpl, periodicity, timespan, period, name };
            }
        }
        return null;
    }

    private parseTimespan(periodicity: FeedbackPeriodicity, timespan: string): string {
        switch(periodicity) {
            case FeedbackPeriodicity.Year:
                if (regexpFullTest(timespan, /\d\d\d\d/)) {
                    return moment(`${timespan}-01`).format('YYYY');
                }
                break;
            case FeedbackPeriodicity.Quarter:
                if (regexpFullTest(timespan, /\d\d\d\d-\d/)) {
                    const split: string[] = timespan.split('-');
                    return `${split[0]} Q${split[1]}`;
                }
                break;
            case FeedbackPeriodicity.Month:
                if (regexpFullTest(timespan, /\d\d\d\d-\d\d/)) {
                    return moment(`${timespan}-01`).format('YYYY MMMM');
                }
                break;
            case FeedbackPeriodicity.Week:
                if (regexpFullTest(timespan, /\d\d\d\d-\d\d/)) {
                    const weekdays: string[] = moment.localeData().weekdays();
                    const first: string = weekdays[0];
                    const last: string = weekdays[weekdays.length - 1];
                    const split: string[] = timespan.split('-');
                    const year: number = Number(split[0]);
                    const week: number = Number(split[1]);
                    const format: string = 'YYYY MMMM DD';
                    const start: moment.Moment = moment().year(year).day(first).week(week);
                    const end: moment.Moment = moment().year(Number(split[0])).day(last).week(week);
                    return `${moment(start).format(format)} - ${moment(end).format(format)}`;
                }
                break;
            case FeedbackPeriodicity.Day:
                if (regexpFullTest(timespan, /\d\d\d\d-\d\d-\d\d/)) {
                    return moment(timespan).format('YYYY MMMM DD');
                }
                break;
        }
        return '';
    }

    private getStatHash(stat: IFeedbackKey): string {
        const m: string = '0000';
        const tpl: string = (stat.tpl ? stat.tpl.id as number : 0).toString();
        const per: string = enumValues(FeedbackPeriodicity).indexOf(stat.periodicity).toString();
        return stat.service + (m + tpl).slice(m.length * -1) + (m + per).slice(m.length * -1) + stat.timespan;
    }

}

const Feedback = new FeedbackSingleton();

export default Feedback;